/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import usePrevious from 'react-use/lib/usePrevious';
import styled from 'styled-components';
import classNames from 'classnames';
import { useField, FieldHelperProps, useFormikContext } from 'formik';
import {
	ComboBox,
	DropDownList,
	ComboBoxChangeEvent,
	DropDownListChangeEvent,
	ComboBoxFilterChangeEvent
} from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import { DateInput, DatePickerModifiers } from '@blueprintjs/datetime';
import ErrorMessage from '../../../commonComponents/ErrorMessage';
import DocumentField from './DocumentField';
import Blob from '../../../models/be/db/Blob';
import {
	single,
	first,
	getLocaleDate,
	getLocaleDateStringInIsoFormat,
	hasValue,
	getLocaleDateFromFormatString,
	safeParseInt,
	getNestedFieldValue,
	getPhoneNumberPartsForNoCountryCode,
	saveBase64File
} from '../../../lib/utils';
import { getCultureString } from '../../../core/internationalisation';
import { evalString, evalNumber, evalDate, evalBoolean } from '../../../core/ast';
import MonthYearField from '../../../commonComponents/fields/MonthYearField';
import Option from '../../../models/Option';
import { populateTemplateIfValuesChanged } from './form';
import DataCaptureFieldProps from '../../../models/dynamicForms/DataCaptureFieldProps';
import { ComboBoxWrapper } from '../../../commonComponents/fields/ComboBoxField';
import {
	CheckboxInputElement,
	CheckboxInputElementFullLength,
	CheckboxLabelText,
	CheckboxWrapper
} from '../fields/Checkbox';
import { baseInputCss, StyledDescription, StyledInput } from '../../../commonComponents/fields/StyledElements';
import { DropDownListWrapper } from '../../../commonComponents/fields/DropDownListField';
import { AstExpression } from '../../../models/be/db/FormSpecification';
import MobilePhoneField from '../../../commonComponents/fields/MobilePhoneField';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import { getFile } from '../../../core/api';
import { SanitizedHTML } from '../rhf/LinkModule';

const DataCaptureField = (props: DataCaptureFieldProps) => {
	const { values: formikValues } = useFormikContext<unknown>();
	const [, , performAsyncAction] = useAsyncAction();
	const fieldName = first(props.fieldNames);
	const componentProps = props.props;
	const contextWithWorkflow = { ...(formikValues as Record<string, unknown>), ...props.visibleWorkflowFields };
	const label = evalString(componentProps.label, contextWithWorkflow);
	const { addTodayLabel } = componentProps;
	const {
		className,
		minLength,
		maxLength,
		min,
		max,
		values: dataValues,
		textField = hasTextAndValueKey(dataValues) ? 'text' : undefined,
		dataItemKey = hasTextAndValueKey(dataValues) ? 'value' : undefined,
		excludedValues,
		staticText,
		richText,
		document
	} = componentProps;
	const type = evalString(componentProps.type, formikValues);
	const pattern = evalString(componentProps.pattern, formikValues);
	const hidden = hasValue(componentProps.hidden) ? evalBoolean(componentProps.hidden, formikValues) : false;

	if (hasValue(excludedValues)) {
		const isOptionArray = hasTextAndValueKey(dataValues);
		excludedValues.forEach((v) => {
			const index = dataValues.findIndex((dv: string | Option<string>) =>
				isOptionArray ? (dv as Option<string>).value === v : dv === v
			);
			const numberOfElementsToDelete = 1;
			// findIndex returns -1 if no match, splice will delete from the end if index is -1.
			if (index >= 0) {
				dataValues.splice(index, numberOfElementsToDelete);
			}
		});
	}
	const [kendoDropDownData, setKendoDropDownData] = React.useState(dataValues);
	const kendoDropDownListProps = { textField, dataItemKey, data: kendoDropDownData };
	const formikProps = { name: fieldName, label, type };
	const [field, meta, helpers] = useField(formikProps);
	const extendFieldName = props.fieldNames.length > 1 ? props.fieldNames[1] : '';
	const extendFormikProps = { name: extendFieldName, label, type };
	const [extendField, , extendHelpers] = useField(extendFormikProps);

	const fieldValue = field.value ?? '';
	if (props.props.forceUppercase && /[a-z]/.test(fieldValue)) {
		helpers.setValue(fieldValue.toUpperCase());
	}

	const initialized = React.useRef(false);
	if (!initialized.current) {
		initialized.current = true;
		const { defaultValue } = props.props;
		if (hasValue(defaultValue) && !hasValue(fieldValue)) {
			const evaluatedValue = Array.isArray(defaultValue)
				? getNestedFieldValue(formikValues, single(defaultValue))
				: defaultValue;
			// Because we can not set Formik values or errors inside of "mounting code", we must do so
			// after the component is mounted by using setTimeout with 0 to append the code to Javascript's event queue.
			setTimeout(() => {
				helpers.setValue(evaluatedValue);
			});
		}
	}

	const inputAttributes = { id: fieldName, className, min, max, pattern } as React.TextareaHTMLAttributes<unknown>;
	if (hasValue(minLength)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const context = useFormikContext();
		inputAttributes.minLength = evalNumber(minLength, context.values);
	}
	if (hasValue(maxLength)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const context = useFormikContext();
		inputAttributes.maxLength = evalNumber(maxLength, context.values);
	}
	const classNamesStr = classNames(isCheckbox(type) ? 'k-checkbox' : 'k-textbox', props.className);

	const filterComboBoxChange = (event: ComboBoxFilterChangeEvent) => {
		const data = filterBy(dataValues as string[], event.filter);
		setKendoDropDownData(data);
	};

	if (hasValue(props.props.contentTemplate)) {
		const template = props.props.contentTemplate;
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const context = useFormikContext();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const previousValues = usePrevious(context.values);
		// We do not want to populate template on mount
		if (hasValue(previousValues)) {
			const populatedTemplate = populateTemplateIfValuesChanged(template, previousValues, context.values);
			if (populatedTemplate !== null) {
				// This code is to prevent fast cypress code from encountering a formik error relating to state being updated in
				//  the Occupations page of the CRIM workflow.
				setTimeout(() => {
					helpers.setValue(populatedTemplate);
				}, 0);
			}
		}
	}

	// This is only for Reference To date field label onClick event.
	const handleAddTodayClick = () => {
		const todayInIsoFormat = getLocaleDateStringInIsoFormat(new Date());
		helpers.setValue(todayInIsoFormat);
	};

	const handleDownload = async (newDocument: Blob) => {
		await performAsyncAction(async () => {
			const fileString = await getFile(newDocument.fileId);
			saveBase64File(fileString, newDocument.originalFileName);
		});
	};

	let element = null;
	let showLabel = !(props.props.hasMultipleLines && type === 'checkbox');

	switch (type) {
		case 'page':
		case 'section':
			element = <></>;
			break;
		case 'text': {
			if (props.props.hasMultipleLines) {
				element = <textarea {...field} value={fieldValue} {...inputAttributes} className={classNamesStr} rows={3} />;
				break;
			}
			const oldOnBlur = field.onBlur;
			field.onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
				if (props.props?.textEffect && props.props?.textEffect === 'upper')
					helpers.setValue(e.target.value.toUpperCase().replace(/\s+/g, ' ').trim());
				else helpers.setValue(e.target.value.replace(/\s+/g, ' ').trim());
				oldOnBlur(e);
			};
			element = (
				<div>
					<StyledInput
						{...field}
						value={fieldValue}
						{...inputAttributes}
						className={classNamesStr}
						type={type}
						autoComplete="nope"
					/>
					{props.props.fieldDescription && <StyledDescription>{props.props.fieldDescription}</StyledDescription>}
				</div>
			);
			break;
		}
		case 'phoneNumber': {
			if (!hasValue(extendField.value) && hasValue(field.value)) {
				extendHelpers.setValue(getPhoneNumberPartsForNoCountryCode(field.value, props.customerCountryCode).countryCode);
			}
			showLabel = false;
			element = (
				<MobilePhoneField
					internationalNumber={fieldValue ?? ''}
					onValueChange={(key: string, value: string) => {
						if (field.name === key) {
							helpers.setValue(value);
						} else if (extendField.name === key) {
							extendHelpers.setValue(value);
						}
					}}
					countryCode={
						hasValue(fieldValue)
							? extendField.value || props.customerCountryCode
							: props.customerCountryCode || props.visibleWorkflowFields?.countryCode
					}
					name={field.name}
					extendName={extendField.name}
					required={false}
					dataCaptureFieldStyling
					supportAllInternational
					label={label}
				/>
			);
			break;
		}
		case 'line':
			showLabel = false;
			element = <SectionSeparator />;
			break;
		case 'staticText':
			showLabel = false;
			element = <Label className={classNames('label')}>{staticText}</Label>;
			break;
		case 'richText': {
			showLabel = false;
			const processedRichText = richText?.replace(/{customerName}/g, props.customerName || '');
			element = (
				<LabelContainer className={classNames('label')}>
					<div dangerouslySetInnerHTML={{ __html: SanitizedHTML(processedRichText) }} />
				</LabelContainer>
			);
			break;
		}
		case 'documentDownload':
			showLabel = false;
			element = <a onClick={() => handleDownload(document)}>{label}</a>;
			break;
		case 'checkbox': {
			if (props.props.hasMultipleLines) {
				if (!fieldValue && props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(true);
				element = (
					<CheckboxWrapper>
						<CheckboxInputElementFullLength
							{...field}
							value={fieldValue}
							{...inputAttributes}
							onChange={(e) => {
								if (!e.target.checked) {
									if (props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(true);
								} else if (props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(false);
								helpers.setValue(e.target.checked);
								field.onChange(e);
							}}
							className={classNamesStr}
							type={type}
						/>
						<CheckboxLabelText>{label}</CheckboxLabelText>
					</CheckboxWrapper>
				);
			} else {
				element = (
					<CheckboxInputElement
						{...field}
						value={fieldValue}
						{...inputAttributes}
						className={classNamesStr}
						type={type}
					/>
				);
			}
			break;
		}
		case 'number':
			element = (
				<StyledInput
					{...field}
					value={fieldValue}
					{...inputAttributes}
					className={classNamesStr}
					type={type}
					onChange={handleNumberInputValueChange(max as number, helpers.setValue)}
					autoComplete="nope"
				/>
			);
			break;
		case 'file':
			element = (
				<DocumentField
					workflowId={props.workflowId}
					candidateId={props.candidateId}
					fieldNames={props.fieldNames}
					{...props.props}
					stripe={props.stripe}
					documentIsDownloadable={props.fileIsDownloadable}
					onFileUpload={props.onFileUpload}
				/>
			);
			break;
		case 'comboBox':
			element = (
				<ComboBoxWrapper>
					<ComboBox
						{...field}
						value={getActualValue(fieldValue, dataValues)}
						{...kendoDropDownListProps}
						popupSettings={{ className }}
						onChange={handleDropdownChange(helpers, dataItemKey)}
						onFilterChange={filterComboBoxChange}
						filterable
					/>
				</ComboBoxWrapper>
			);
			break;
		case 'dropDownList':
			element = (
				<DropDownListWrapper width={undefined}>
					<DropDownList
						{...field}
						value={getActualValue(fieldValue, dataValues)}
						{...kendoDropDownListProps}
						popupSettings={{ className }}
						onChange={handleDropdownChange(helpers, dataItemKey)}
					/>
					{field.name === 'dbsDetails.faRole' && <BarredList>Barred lists : {fieldValue.split('|').pop()}</BarredList>}
				</DropDownListWrapper>
			);
			break;
		case 'monthYear': {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const onValueChange = (name: string, updatedValue: string | boolean, errorMessage: string) => {
				helpers.setValue(updatedValue);
			};
			element = <MonthYearField {...field} onValueChange={onValueChange} />;
			break;
		}
		case 'date': {
			let defaultMin = new Date(1901, 0, 1); // ACIC Checks dates must be in 1901 or later.
			let defaultMax = new Date(2100, 0, 1);

			if (hasValue(max)) {
				defaultMax = getLocaleDate(
					getLocaleDateStringInIsoFormat(evalDate(max as string | AstExpression, formikValues))
				);
			}

			if (hasValue(min)) {
				const evaluatedMin = evalDate(min as string | AstExpression, formikValues);
				if (typeof evaluatedMin === 'string') {
					const normalizedMinString = (evaluatedMin as string).replace(/--/g, '01');
					defaultMin = getLocaleDate(normalizedMinString) ?? defaultMin;
				} else {
					defaultMin = getLocaleDate(getLocaleDateStringInIsoFormat(evaluatedMin)) ?? defaultMin;
				}
			}

			let dateValue = getLocaleDate(field.value);
			// We have set the date to null otherwise the Blueprint DateInput component will encounter this error:
			// value prop must be within minDate and maxDate bounds.
			if (dateValue !== null && (dateValue < defaultMin || defaultMax < dateValue)) {
				// We must remove the incorrect value stored in formik, but we can't do that directly within rendering code.
				setTimeout(() => {
					helpers.setValue(null);
				}, 0);
				dateValue = null;
			}
			element = (
				<DateInputWrapper>
					<DateInput
						value={dateValue}
						formatDate={(date) => date.toLocaleDateString(getCultureString())}
						parseDate={(str) => getLocaleDateFromFormatString(str, 'dd/MM/yyyy')}
						minDate={defaultMin}
						maxDate={defaultMax}
						onChange={(date) => {
							helpers.setValue(getLocaleDateStringInIsoFormat(date));
						}}
						fill
						modifiers={blueprintModifiers}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						inputProps={inputAttributes as any}
						disabled={false}
					/>
				</DateInputWrapper>
			);

			break;
		}
		case 'checkList': {
			const currentValues: string[] = Array.isArray(fieldValue) ? fieldValue : [];
			const isOptionArray = hasTextAndValueKey(dataValues);

			const handleCheckListChange = (itemValue: string, checked: boolean) => {
				let newValues: string[];
				if (checked) {
					newValues = [...currentValues, itemValue];
				} else {
					newValues = currentValues.filter((v) => v !== itemValue);
				}
				helpers.setValue(newValues);
			};

			element = (
				<CheckListWrapper>
					{(dataValues as (string | Option<string>)[])?.map((item, index) => {
						const itemValue = isOptionArray ? (item as Option<string>).value : (item as string);
						const itemText = isOptionArray ? (item as Option<string>).text : (item as string);
						const isChecked = currentValues.includes(itemValue);
						const itemId = `${fieldName}-${index}`;

						return (
							<CheckboxWrapper key={itemValue}>
								<CheckboxInputElement
									id={itemId}
									type="checkbox"
									className="k-checkbox"
									checked={isChecked}
									onChange={(e) => handleCheckListChange(itemValue, e.target.checked)}
								/>
								<label className="k-checkbox-label" htmlFor={itemId}>
									{itemText}
								</label>
							</CheckboxWrapper>
						);
					})}
				</CheckListWrapper>
			);
			break;
		}
		default:
			throw new Error(`Unknown type: ${type}.`);
	}

	return (
		<MainWrapper
			type={type}
			hasMultipleLines={!!props.props.hasMultipleLines}
			className={classNames(
				type === 'line' ? 'k-form-divider' : 'k-form-field',
				((type === 'text' || type === 'checkbox') && props.props.hasMultipleLines) ||
					type === 'staticText' ||
					type === 'richText'
					? 'longText'
					: null,
				hidden ? 'hidden' : null
			)}
			data-fieldname={fieldName}
		>
			{/* Must render checkbox input before label, otherwise css won't work. */}
			{isCheckbox(type) && element}
			{showLabel && !props.props.hasMultipleLines && (
				<Label
					className={classNames(
						'label',
						type === 'checkbox' ? 'k-checkbox-label' : null,
						addTodayLabel ? 'inlineLabel' : null
					)}
					title={componentProps.title || ''}
					htmlFor={fieldName}
				>
					{label}
				</Label>
			)}
			{addTodayLabel && (
				<div
					className={classNames('label inlineLabel secondLabel', componentProps.className)}
					onClick={handleAddTodayClick}
				>
					{addTodayLabel}
				</div>
			)}
			{!isCheckbox(type) && element}
			{meta.touched && <ErrorMessage message={meta.error} />}
		</MainWrapper>
	);
};

DataCaptureField.defaultProps = {
	fileIsDownloadable: false
};

export default DataCaptureField;

const blueprintModifiers: DatePickerModifiers = {
	blueprint: () => true
};

const handleDropdownChange =
	(helpers: FieldHelperProps<unknown>, dataItemKey: string) =>
	(event: DropDownListChangeEvent | ComboBoxChangeEvent) => {
		const { value } = event.target;
		helpers.setValue(
			hasValue(value) && (value as Record<string, unknown>)[dataItemKey] !== undefined ? value[dataItemKey] : value
		);
	};

const getActualValue = (value: string | Option<string>, data: string[] | Option<string>[]) => {
	if (!value) return value;
	return data &&
		data[0] &&
		(data[0] as Option<string>).value !== undefined &&
		(value as Option<string>).value === undefined
		? (data as Option<string>[]).find((x) => x.value === value)
		: value;
};

// This is based on options format: [{ value: 'AUS', text: 'Australia' }].
const hasTextAndValueKey = (options: string[] | Option<string>[]) => {
	return (
		options?.length > 0 &&
		(options[0] as Option<string>).text !== undefined &&
		(options[0] as Option<string>).value !== undefined
	);
};

const isCheckbox = (type: string) => type === 'checkbox';

// Does not support for negative number of min and max.
const handleNumberInputValueChange =
	(max: number, setValue: (newValue: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const valueNumber = safeParseInt(e.target.value);
		if (valueNumber <= max) {
			setValue(valueNumber);
		}
	};

const MainWrapper = styled.div<{ type: string; hasMultipleLines: boolean }>`
	${(props) => (props.type === 'checkbox' && !props.hasMultipleLines ? '	position: relative; top: 37px' : '')}
`;

const Label = styled.label`
	display: block;
`;

const LabelContainer = styled.div`
	display: block;
`;

const DateInputWrapper = styled.div`
	&& {
		.bp4-overlay,
		.bp4-dateinput-popover {
			z-index: 102;
			position: absolute;
		}

		.bp3-input,
		.bp4-input {
			${baseInputCss}
			box-shadow: none;
		}

		.bp3-popover-wrapper {
			width: unset;
		}
	}
`;

const BarredList = styled.label`
	margin-top: 10px;
	display: block;
	opacity: 0.65;
	color: #262626;
`;

const SectionSeparator = styled.hr`
	border: 0;
	margin: 0;
	padding: 0;
	height: 1px;
	border-top: 1px solid #ccc;
`;

const CheckListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
